/**
 * Query keys of one organization. Everything lives under ['org', orgId] so switching clinics can
 * drop it all. Audited clinical reads (medical profile, notes, treatment details) live under
 * 'clinical' and are never refetched by live updates or broad invalidation: every read of them
 * writes an audit row on the server.
 */
export const keys = {
  org: (orgId: string) => ['org', orgId] as const,
  patients: (orgId: string) => ['org', orgId, 'patients'] as const,
  patient: (orgId: string, id: string) => ['org', orgId, 'patients', 'one', id] as const,
  appointments: (orgId: string) => ['org', orgId, 'appointments'] as const,
  visits: (orgId: string) => ['org', orgId, 'visits'] as const,
  /** Under 'visits': a change to the visit (live event) refetches its materials too. */
  visitMaterials: (orgId: string, visitId: string) => ['org', orgId, 'visits', 'materials', visitId] as const,
  inventory: (orgId: string) => ['org', orgId, 'inventory'] as const,
  /** Balances, the books and reports: every journal change makes them stale. */
  finance: (orgId: string) => ['org', orgId, 'finance'] as const,
  account: (orgId: string, patientId: string) => ['org', orgId, 'finance', 'account', patientId] as const,
  services: (orgId: string) => ['org', orgId, 'services'] as const,
  resources: (orgId: string) => ['org', orgId, 'resources'] as const,
  practitioners: (orgId: string) => ['org', orgId, 'practitioners'] as const,
  /** Whether the clinic checks patients in (ADR-035). */
  bookingSettings: (orgId: string) => ['org', orgId, 'booking-settings'] as const,
  /** Who handles the laser desk and till, and the sections the caller reaches (ADR-036). */
  sectionSettings: (orgId: string) => ['org', orgId, 'section-settings'] as const,
  templates: (orgId: string) => ['org', orgId, 'treatment-templates'] as const,
  methods: (orgId: string) => ['org', orgId, 'treatment-methods'] as const,
  smsChannels: (orgId: string) => ['org', orgId, 'sms-channels'] as const,
  smsQueue: (orgId: string) => ['org', orgId, 'sms-queue'] as const,
  smsTemplates: (orgId: string) => ['org', orgId, 'sms-templates'] as const,
  members: (orgId: string) => ['org', orgId, 'members'] as const,
  patientImports: (orgId: string) => ['org', orgId, 'patient-imports'] as const,
  dataExports: (orgId: string) => ['org', orgId, 'data-exports'] as const,
  audit: (orgId: string) => ['org', orgId, 'audit'] as const,
  clinical: (orgId: string) => ['org', orgId, 'clinical'] as const,
  medical: (orgId: string, patientId: string) => ['org', orgId, 'clinical', 'medical', patientId] as const,
  notes: (orgId: string, patientId: string) => ['org', orgId, 'clinical', 'notes', patientId] as const,
  visitDetail: (orgId: string, visitId: string) => ['org', orgId, 'clinical', 'visit', visitId] as const,
  /** Outside 'visits' on purpose: completing a visit must not refetch its own preview. */
  visitPreview: (orgId: string, visitId: string) => ['org', orgId, 'visit-preview', visitId] as const,
};
